import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Copy, Download, Pause, Play, Terminal } from "lucide-react";
import { api, post } from "../../api";
import type { Role } from "../../types";

interface LogChunk {
  text: string;
  cursor: number;
  bootId: string;
  reset: boolean;
  truncated: boolean;
}

interface ConsoleLine {
  id: number;
  text: string;
  kind: string;
}

interface CommandResult {
  message: string;
  operationId: string;
  data?: { count?: number; players?: Array<{ characterName?: string; platformId?: string }> };
}

const commandReference = [
  { name: "players", syntax: "players", description: "Lista somente as sessões de jogadores confirmadas pelo rastreador.", vanilla: true },
  { name: "broadcast", syntax: "broadcast <mensagem>", description: "Envia um aviso para todos os jogadores.", vanilla: false },
  { name: "save", syntax: "save", description: "Solicita ao jogo um salvamento imediato do mundo.", vanilla: false },
  { name: "kick", syntax: "kick <jogador>", description: "Desconecta um jogador identificado de forma inequívoca.", vanilla: false },
  { name: "ban", syntax: "ban <jogador>", description: "Bloqueio nativo pela Platform ID; provider estruturado ainda não está disponível.", vanilla: false, unsupported: true },
  { name: "unban", syntax: "unban <ID>", description: "Remoção da lista de bloqueio; provider estruturado ainda não está disponível.", vanilla: false, unsupported: true },
  { name: "player_info", syntax: "player_info <jogador>", description: "Consulta detalhes expostos com segurança pelo bridge.", vanilla: false },
  { name: "item_drop", syntax: "item_drop <jogador> <item>", description: "Entrega no mundo; ainda não existe provider autoritativo nesta build.", vanilla: false, unsupported: true },
  { name: "spawn", syntax: "spawn <prefab>", description: "Criação de prefab ainda não existe nesta build.", vanilla: false, unsupported: true },
  { name: "teleport", syntax: "teleport <jogador> <destino>", description: "Use a seção de administração do jogador para o teleporte com ACK.", vanilla: false, unsupported: true },
];

const maxLines = 2_000;

export function ConsoleView({ serverId, role }: { serverId: string; role?: Role }) {
  const [lines, setLines] = useState<ConsoleLine[]>([]);
  const [connected, setConnected] = useState(false);
  const [paused, setPaused] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [autoscroll, setAutoscroll] = useState(true);
  const [command, setCommand] = useState("");
  const [commandResult, setCommandResult] = useState("");
  const [sending, setSending] = useState(false);
  const [availableCommands, setAvailableCommands] = useState<string[]>(["players"]);
  const nextID = useRef(1);
  const partial = useRef("");
  const output = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const pausedQueue = useRef<LogChunk[]>([]);

  const append = useCallback((chunk: LogChunk, force = false) => {
    if (pausedRef.current && !force) {
      pausedQueue.current.push(chunk);
      while (pausedQueue.current.reduce((size, item) => size + item.text.length, 0) > 512 << 10) pausedQueue.current.shift();
      return;
    }
    if (chunk.reset) {
      setLines([]);
      partial.current = "";
    }
    const combined = partial.current + chunk.text;
    const parts = combined.split(/\r?\n/);
    partial.current = parts.pop() ?? "";
    if (!parts.length) return;
    const added = parts.map((text) => ({ id: nextID.current++, text, kind: classifyLog(text) }));
    setLines((current) => [...current, ...added].slice(-maxLines));
  }, []);
  useEffect(() => {
    pausedRef.current = paused;
    if (!paused && pausedQueue.current.length) {
      const queued = pausedQueue.current.splice(0);
      queued.forEach((chunk) => append(chunk, true));
    }
  }, [append, paused]);
  useEffect(() => {
    let source: EventSource | undefined;
    let cancelled = false;
    void api<LogChunk>(`/servers/${serverId}/console/history?limit=${128 << 10}`).then((history) => {
      if (cancelled) return;
      append(history);
      if (typeof EventSource === "undefined") return;
      const params = new URLSearchParams({ cursor: String(history.cursor), bootId: history.bootId });
      source = new EventSource(`/api/v1/servers/${serverId}/console/stream?${params}`);
      source.onopen = () => setConnected(true);
      source.onerror = () => setConnected(false);
      source.addEventListener("log", (event) => {
        try { append(JSON.parse((event as MessageEvent).data) as LogChunk); } catch { /* reconnect will resume from the last event id */ }
      });
      source.addEventListener("console-error", () => setConnected(false));
    }).catch(() => setConnected(false));
    return () => { cancelled = true; source?.close(); };
  }, [append, serverId]);

  useEffect(() => {
    let active = true;
    void api<{ availableCommands?: string[] }>(`/servers/${serverId}/admin-bridge`).then((status) => {
      if (active && Array.isArray(status.availableCommands)) setAvailableCommands(status.availableCommands);
    }, () => undefined);
    return () => { active = false; };
  }, [serverId]);

  useEffect(() => {
    if (autoscroll && output.current) output.current.scrollTop = output.current.scrollHeight;
  }, [autoscroll, lines]);

  const visible = useMemo(() => lines.filter((line) => (filter === "all" || line.kind === filter) && (!query || line.text.toLowerCase().includes(query.toLowerCase()))), [filter, lines, query]);
  const run = async () => {
    const value = command.trim();
    if (!value) return;
    const [name, ...rest] = value.split(/\s+/);
    setSending(true);
    setCommandResult("Executando…");
    try {
      const result = await post<CommandResult>(`/servers/${serverId}/console/commands`, { command: name, arguments: rest.length ? { text: rest.join(" ") } : {} });
      const playerSummary = name.toLowerCase() === "players" && result.data
        ? ` ${result.data.count ?? 0} online${result.data.players?.length ? `: ${result.data.players.map((player) => player.characterName || player.platformId || "identidade pendente").join(", ")}` : ""}.`
        : "";
      setCommandResult(`${result.message}${playerSummary} · operação ${result.operationId.slice(0, 8)}`);
      setCommand("");
    } catch (reason) {
      setCommandResult(reason instanceof Error ? reason.message : "O comando não pôde ser executado.");
    } finally {
      setSending(false);
    }
  };
  const download = () => {
    const blob = new Blob([visible.map((line) => line.text).join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `valheim-console-${serverId.slice(0, 8)}.log`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return <section className="panel console-panel">
    <div className="panel-head"><div><span className="eyebrow">SERVIDOR</span><h2>Console do Valheim</h2></div><Terminal size={20}/></div>
    <div className="console-toolbar">
      <span className={connected ? "console-status connected" : "console-status"}>{connected ? "Conectado" : "Reconectando"}</span>
      <input aria-label="Buscar no console" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar nas linhas"/>
      <select aria-label="Filtrar console" value={filter} onChange={(event) => setFilter(event.target.value)}><option value="all">Todos</option><option value="system">Sistema</option><option value="connection">Conexão</option><option value="players">Jogadores</option><option value="bepinex">BepInEx/mods</option><option value="warning">Avisos</option><option value="error">Erros</option><option value="world">Mundo/save</option></select>
      <button className="secondary small" onClick={() => setPaused(!paused)}>{paused ? <Play size={14}/> : <Pause size={14}/>} {paused ? "Retomar" : "Pausar"}</button>
      <button className="secondary small" onClick={() => setLines([])}>Limpar tela</button>
      <button className="secondary small" onClick={download}><Download size={14}/> Baixar recorte</button>
      <label className="console-autoscroll"><input type="checkbox" checked={autoscroll} onChange={(event) => setAutoscroll(event.target.checked)}/> Rolagem automática</label>
    </div>
    <div className="console-output" ref={output} role="log" aria-live="off">{visible.map((line) => <div className={`console-line ${line.kind}`} key={line.id}><time>{lineTime(line.text)}</time><code>{line.text}</code><button aria-label="Copiar linha" title="Copiar linha" onClick={() => void navigator.clipboard.writeText(line.text)}><Copy size={12}/></button></div>)}{!visible.length && <div className="console-empty">Nenhuma linha corresponde ao filtro atual.</div>}</div>
    {role !== "viewer" && <div className="game-command"><span aria-hidden="true">›</span><input value={command} onChange={(event) => setCommand(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") void run(); }} placeholder="Comando estruturado, por exemplo: players"/><button className="primary small" disabled={sending || !command.trim()} onClick={() => void run()}>{sending ? "Executando…" : "Executar"}</button></div>}
    <small className="field-hint">Em servidor vanilla, apenas <b>players</b> está disponível. Outros comandos são habilitados somente após um Admin Bridge compatível — isto nunca executa comandos do Linux.</small>
    <details className="command-reference">
      <summary>Ver comandos e o que cada um faz</summary>
      <div className="command-reference-list">
        {commandReference.map((item) => {
          const available = !item.unsupported && (availableCommands.includes(item.name) || (item.vanilla && availableCommands.length === 0));
          return <div className="command-reference-row" key={item.name}>
            <code>{item.syntax}</code>
            <span>{item.description}</span>
            <small className={available ? "available" : "unavailable"}>{available ? "Disponível agora" : item.unsupported ? "Indisponível nesta build" : "Exige bridge compatível"}</small>
          </div>;
        })}
      </div>
    </details>
    {commandResult && <div className="notice">{commandResult}</div>}
  </section>;
}

function classifyLog(line: string) {
  const value = line.toLowerCase();
  if (value.includes("error") || value.includes("exception") || value.includes("fatal")) return "error";
  if (value.includes("warning") || value.includes("warn")) return "warning";
  if (value.includes("bepinex") || value.includes("plugin") || value.includes("mod")) return "bepinex";
  if (value.includes("player") || value.includes("peer") || value.includes("character")) return "players";
  if (value.includes("world") || value.includes("save")) return "world";
  if (value.includes("connect") || value.includes("session") || value.includes("socket")) return "connection";
  return "system";
}

function lineTime(line: string) {
  const match = line.match(/(?:^|\s)(\d{2}:\d{2}:\d{2})(?:\s|$)/);
  return match?.[1] ?? "";
}
